import { SettingsPermissions } from "@redotech/account-common/permissions";
import { IterableMap } from "@redotech/react-util/component";
import { useHandler } from "@redotech/react-util/hook";
import { useTranslation } from "@redotech/redo-i18n";
import DownArrowIcon from "@redotech/redo-icons/arrow-down.svg";
import UpArrowIcon from "@redotech/redo-icons/arrow-up.svg";
import TrashIcon from "@redotech/redo-icons/trash.svg";
import { useHasPermission } from "@redotech/redo-merchant-app-common/auth/permissions-context";
import {
  ReentryChoiceState,
  ReentryState,
} from "@redotech/redo-return-ui/flows/chat-flow/reentry";
import { RequiredMessage } from "@redotech/redo-return-ui/flows/required-message";
import * as multipleChoiceCss from "@redotech/redo-return-ui/flows/return-flow/multiple-choice.module.css";
import {
  StepId,
  StepTypeDetailsProps,
} from "@redotech/redo-return-ui/flows/step";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { LabeledInput } from "@redotech/redo-web/labeled-input";
import { InputTheme, TextInput } from "@redotech/redo-web/text-input";
import { produce } from "immer";
import { memo } from "react";

export const ReentryDetails = memo(function ReentryDetails({
  stepSelect,
  state,
  setState,
}: StepTypeDetailsProps<ReentryState>) {
  const { t } = useTranslation();
  // TODO: Not sure what the right permission mix is
  const canEditSupportSettings = useHasPermission(
    SettingsPermissions.SET_SUPPORT_SETTINGS,
  );
  const canEditReturnsSettings = useHasPermission(
    SettingsPermissions.SET_RETURNS_AND_CLAIMS_SETTINGS,
  );
  const canEditSettings = canEditSupportSettings || canEditReturnsSettings;

  const handleAdd = useHandler(() =>
    setState((state) => ({
      ...state,
      choices: [...state.choices, { name: "", next: undefined }],
    })),
  );
  const handleFocus = useHandler((focused: boolean) => {
    if (!focused) {
      setState((state) => ({ ...state, attempted: true }));
    }
  });

  return (
    <>
      <IterableMap items={state.choices} keyFn={(_, index) => index}>
        {(choice, index) => (
          <StateChoiceComponent
            canEditSettings={canEditSettings}
            choice={choice}
            handleFocus={handleFocus}
            index={index}
            key={index}
            setState={setState}
            state={state}
            stepSelect={stepSelect}
          />
        )}
      </IterableMap>
      <Divider />
      <div>
        <RedoButton
          disabled={!canEditSettings}
          hierarchy="secondary"
          onClick={handleAdd}
          size="sm"
          text={t("+ Add option", {
            context: "button label; keep the leading plus sign",
          })}
          theme="normal"
        />
      </div>
      {!state.choices.length && (
        <RequiredMessage>{t("Must have at least one option")}</RequiredMessage>
      )}
    </>
  );
});

const StateChoiceComponent = memo(function StateChoiceComponent({
  state,
  setState,
  stepSelect,
  handleFocus,
  canEditSettings,
  choice,
  index,
}: {
  state: ReentryState;
  setState: StepTypeDetailsProps<ReentryState>["setState"];
  stepSelect: StepTypeDetailsProps<ReentryState>["stepSelect"];
  handleFocus(focused: boolean): void;
  canEditSettings: boolean;
  choice: ReentryChoiceState;
  index: number;
}) {
  const { t } = useTranslation();
  const setName = useHandler((name: string) =>
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.choices[index].name = name;
      }),
    ),
  );
  const setStep = useHandler((next: StepId | undefined) =>
    setState((state) =>
      produce(state, (state) => {
        // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
        state.choices[index].next = next;
      }),
    ),
  );
  const handleUp = useHandler(() => {
    setState((state) =>
      produce(state, (state) => {
        const choices = state.choices.splice(index, 1);
        state.choices.splice(index - 1, 0, ...choices);
      }),
    );
  });
  const handleDown = useHandler(() => {
    setState((state) =>
      produce(state, (state) => {
        const choices = state.choices.splice(index, 1);
        state.choices.splice(index + 1, 0, ...choices);
      }),
    );
  });
  const handleDelete = useHandler(() => {
    setState((state) =>
      produce(state, (state) => {
        state.choices.splice(index, 1);
      }),
    );
  });
  return (
    <>
      <Divider />
      <LabeledInput
        label={
          <div className={multipleChoiceCss.label}>
            <div>{t("Option #{{number}}", { number: index + 1 })}</div>
            {canEditSettings && (
              <>
                <RedoButton
                  aria-label={t("Move up")}
                  disabled={!index}
                  hierarchy="tertiary"
                  IconLeading={UpArrowIcon}
                  onClick={(event) => {
                    handleUp();
                    event.stopPropagation();
                  }}
                  size="sm"
                  theme="normal"
                />
                <RedoButton
                  aria-label={t("Move down")}
                  disabled={index === state.choices.length - 1}
                  hierarchy="tertiary"
                  IconLeading={DownArrowIcon}
                  onClick={(event) => {
                    handleDown();
                    event.stopPropagation();
                  }}
                  size="sm"
                  theme="normal"
                />
                <RedoButton
                  aria-label={t("Delete")}
                  hierarchy="tertiary"
                  IconLeading={TrashIcon}
                  onClick={(event) => {
                    handleDelete();
                    event.stopPropagation();
                  }}
                  size="sm"
                  theme="normal"
                />
              </>
            )}
          </div>
        }
      >
        <TextInput
          error={state.attempted && !choice.name}
          onChange={setName}
          onFocus={(focused: boolean) => {
            if (!choice.name) {
              handleFocus(focused);
            }
          }}
          readonly={!canEditSettings}
          theme={InputTheme.FORM}
          value={choice.name}
        />
      </LabeledInput>
      <LabeledInput
        errors={state.attempted && !choice.next ? [t("Required")] : []}
        label={t("Next step #{{number}}", { number: index + 1 })}
      >
        {stepSelect({ value: choice.next, valueChange: setStep })}
      </LabeledInput>
    </>
  );
});
